import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const styleRef = useRef(null);
  const headRef = useRef(null);
  const { business } = useBusiness();
  // Re-fetch settings whenever the active tenant changes — critical for
  // multi-tenant preview (`?biz=<slug>` switches), otherwise the
  // SiteLayout footer keeps the previous tenant's address/phone/hours.
  const bid = business?.id;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setSettings(data);
      } catch (_e) {
        if (!cancelled) setSettings({});
      }
    })();
    return () => { cancelled = true; };
  }, [bid]);

  useEffect(() => {
    if (!settings) return;

    // Inject custom CSS
    if (!styleRef.current) {
      styleRef.current = document.createElement("style");
      styleRef.current.setAttribute("data-bella-custom", "css");
      document.head.appendChild(styleRef.current);
    }
    styleRef.current.textContent = settings.custom_css || "";

    // Inject custom head HTML
    if (!headRef.current) {
      headRef.current = document.createElement("div");
      headRef.current.setAttribute("data-bella-custom", "head");
      document.head.appendChild(headRef.current);
    }
    headRef.current.innerHTML = settings.custom_head_html || "";

    // Document title — skip on builder/standalone admin tools and on
    // public micro-product pages that set their own descriptive titles
    // (HustleHydro, HustleShop showcase, etc.).
    if (settings.restaurant_name) {
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      const standalone = /^\/(agency\/admin\/builder|eats\/floorplan|hydro|showcase|account\/credits)/.test(path);
      // Module + umbrella domains own their branding — see ModuleBranding.jsx
      let onModuleHost = false;
      try {
        const { getBrandHost } = require("@/lib/moduleHost");
        onModuleHost = !!getBrandHost();
      } catch { /* noop */ }
      if (!standalone && !onModuleHost) document.title = settings.restaurant_name;
    }
  }, [settings]);

  return <SettingsContext.Provider value={settings}>{children}</SettingsContext.Provider>;
}

export const useSettings = () => useContext(SettingsContext);
